<!--
  PLAYBACK AND THIS BROWSER — the half of the old Settings screen that survived.

  Settings and Profile are one page now: there is no separate destination for
  "your account" and "your preferences", because there was never a reason for a
  person to know which of the two a given control lived on. This component is
  what the merge moved, kept whole so the diff is a move rather than a rewrite.

  The two sections that did NOT survive:
   - Groups. Rooms are not a thing a user manages any more; you join a friend.
   - Cast photos. The TMDB key is gone — cast portraits come from the
     free-for-any-use sources and need no key from anybody.
-->
<script lang="ts">
  import Button from '$lib/ui/Button.svelte';
  import { health, signOut, type Health } from '$lib/ui/api';
  import { ago } from '$lib/ui/format';
  import { language } from '$lib/player/format';
  import { normalizeTrackLanguage } from '../track-language';
  import { prefs, progress } from '$lib/ui/prefs.svelte';
  import { THEMES, chooseTheme, theme } from '$lib/ui/theme.svelte';

  const LANGS = ['', 'en', 'fr', 'es', 'de', 'it', 'pt', 'ru', 'ja', 'ko', 'zh', 'hi', 'nl', 'pl'];
  const audioValue = $derived(prefs.audioLang ? normalizeTrackLanguage(prefs.audioLang) : '');
  const subtitleValue = $derived(prefs.subtitleLang ? normalizeTrackLanguage(prefs.subtitleLang) : '');
  const QUALITIES = ['480p', '720p', '1080p', '2160p'] as const;

  let info = $state<Health | null>(null);

  $effect(() => {
    void health()
      .then((h) => (info = h))
      .catch(() => (info = null));
  });

  async function leave(): Promise<void> {
    try {
      await signOut();
    } finally {
      location.href = '/';
    }
  }
</script>

<section>
  <h2>Theme</h2>
  <!--
    Colour only. A theme cannot change a radius, a duration or a layout number —
    it patches the palette over app.css and nothing else — so picking one can
    never move a control or resize a card.

    JUST THE NAMES, in pills. There were swatch cards here — four colour chips
    and a one-line description per theme — and the owner cut them: choosing a
    theme repaints the WHOLE SCREEN the moment it is pressed, which is a truer
    preview than any strip of chips. The chosen pill reads by LUMINANCE
    (--focus-ring mixes), never by --accent — the house rule for every
    selection state in this app.

    TWO PILLS NOW, Dark and Light: one brand, two grounds. Until one is
    pressed the app follows the operating system, so a viewer who has never
    opened this screen still gets the right ground at breakfast and at night.
  -->
  <div class="themes" role="radiogroup" aria-label="Theme">
    {#each THEMES as t (t.id)}
      <button
        type="button"
        class="pill"
        role="radio"
        aria-checked={theme.id === t.id}
        onclick={() => chooseTheme(t.id)}
      >
        {t.name}
      </button>
    {/each}
  </div>
</section>

<section>
  <h2>Playback</h2>

  <label class="field">
    <span>Preferred audio</span>
    <select value={audioValue} onchange={(e) => { prefs.audioLang = e.currentTarget.value; }}>
      {#if audioValue && !LANGS.includes(audioValue)}<option value={audioValue}>{language(audioValue)}</option>{/if}
      {#each LANGS as tag (tag)}
        <option value={tag}>{tag ? language(tag) : "The file's default"}</option>
      {/each}
    </select>
  </label>

  <label class="field">
    <span>Preferred subtitles</span>
    <select value={subtitleValue} onchange={(e) => { prefs.subtitleLang = e.currentTarget.value; }}>
      {#if subtitleValue && !LANGS.includes(subtitleValue)}<option value={subtitleValue}>{language(subtitleValue)}</option>{/if}
      {#each LANGS as tag (tag)}
        <option value={tag}>{tag ? language(tag) : 'None'}</option>
      {/each}
    </select>
  </label>

  <label class="toggle">
    <input type="checkbox" bind:checked={prefs.subtitlesOn} />
    <span>
      <b>Subtitles on by default</b>
      <em>When the file has a track in your language.</em>
    </span>
  </label>

  <label class="field">
    <span>Prefer up to</span>
    <select bind:value={prefs.maxQuality}>
      {#each QUALITIES as q (q)}
        <option value={q}>{q}</option>
      {/each}
    </select>
  </label>
  <p class="note">
    Automatic playback stays at or below this quality. You can choose another quality
    in the player; we check compatible copies and prefer efficient files.
  </p>
</section>

<section>
  <h2>This browser</h2>
  <dl class="facts">
    <dt>Resume points</dt>
    <dd>{progress.items.length}</dd>
    <dt>App build</dt>
    <dd class="mono">{String(__APP_VERSION__).slice(0, 12)}</dd>
    {#if info}
      <dt>Server</dt>
      <dd class="mono">{info.version.slice(0, 12)}</dd>
      <dt>Up since</dt>
      <dd>{ago(Date.now() - info.uptimeSec * 1000)}</dd>
      <dt>Link source</dt>
      <dd class:warn={info.catalog !== 'configured'}>{info.catalog}</dd>
    {/if}
  </dl>
  <Button variant="ghost" onclick={leave}>Forget the invite on this device</Button>
</section>

<style>
  .themes {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
  }
  .pill {
    display: grid;
    place-content: center;
    min-height: 38px;
    padding-inline: var(--space-4);
    border-radius: var(--radius-pill);
    border: 1px solid var(--line);
    background: var(--surface-1);
    color: var(--text-dim);
    font: inherit;
    font-size: var(--text-sm);
    font-weight: var(--weight-semi);
    letter-spacing: var(--tracking-snug);
    cursor: pointer;
    transition:
      border-color var(--dur-fast) var(--ease),
      background-color var(--dur-fast) var(--ease),
      color var(--dur-fast) var(--ease);
  }
  .pill:hover {
    background: var(--surface-2);
    color: var(--text);
  }
  /* The chosen one reads by LUMINANCE — lighter than its neighbours, never
     pinker. --focus-ring mixes, exactly like the nav's "here" pill. */
  .pill[aria-checked='true'] {
    border-color: color-mix(in oklab, var(--focus-ring) 46%, var(--line));
    background: color-mix(in oklab, var(--focus-ring) 10%, var(--surface-1));
    color: var(--text);
  }
  .pill:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }
  /* A thumb needs the full floor. */
  @media (pointer: coarse) {
    .pill {
      min-height: var(--hit);
    }
  }

      section {
    padding: 20px 0;
    border-bottom: 1px solid var(--line);
  }
  section:last-of-type {
    border-bottom: 0;
  }
  h2 {
    font-size: 0.78rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: var(--text-faint);
    margin-bottom: 12px;
  }

  .field {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    padding: 9px 0;
    font-size: 0.87rem;
  }
  .field > span {
    color: var(--text-dim);
  }
  select {
    flex: 0 1 240px;
    min-width: 0;
    height: 36px;
    padding-inline: 11px;
    border-radius: var(--radius);
    border: 1px solid var(--line);
    background: var(--surface-1);
    color: var(--text);
    font-size: 0.86rem;
    outline: none;
  }
  select:focus {
    border-color: color-mix(in oklab, var(--focus-ring) 60%, var(--line));
  }

  .toggle {
    display: flex;
    align-items: flex-start;
    gap: 11px;
    padding: 11px 0;
    cursor: pointer;
  }
  .toggle input {
    width: 18px;
    height: 18px;
    margin-top: 2px;
    accent-color: var(--accent);
    flex: none;
  }

  /**
   * THE CONTROLS ON THIS SCREEN WERE THE SMALLEST IN THE APP: an 18 px checkbox,
   * three 36 px selects, and a text field at 36. The label is already a `<label>`
   * wrapping the whole row, so the checkbox's real target is the row — but the box
   * itself is what a thumb aims at, and 18 px is a dot.
   */
  @media (pointer: coarse) {
    select {
      height: var(--hit);
    }
    .toggle input {
      width: 22px;
      height: 22px;
    }
    /* The row is the target; make sure it is a whole one. */
    .toggle {
      min-height: var(--hit);
      align-items: center;
    }
  }
  .toggle b {
    display: block;
    font-size: 0.87rem;
    font-weight: 500;
  }
  .toggle em {
    display: block;
    color: var(--text-faint);
    font-style: normal;
    font-size: 0.78rem;
    line-height: 1.45;
  }

  .note {
    color: var(--text-faint);
    font-size: 0.77rem;
    line-height: 1.5;
    margin-top: 6px;
    max-width: 60ch;
  }



  .facts {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 5px 16px;
    margin: 0 0 16px;
    font-size: 0.82rem;
  }
  .facts dt {
    color: var(--text-faint);
  }
  .facts dd {
    margin: 0;
    min-width: 0;
    /* A build hash has no break opportunity, so its min-content width became the
       page's: 293px of unbreakable text made the whole document scroll sideways
       below 375px. Truncated above, breakable here, so neither can happen again. */
    overflow-wrap: anywhere;
    color: var(--text-dim);
  }
  .facts .mono {
    font-family: var(--mono);
    font-size: 0.76rem;
  }
  .facts .warn {
    color: var(--warn);
  }

  /* On a narrow phone "Preferred subtitles" and its control cannot share a line
     without both wrapping; stack them instead. */
  @media (max-width: 480px) {
    .field {
      display: grid;
      gap: 6px;
    }
    select {
      flex: none;
      width: 100%;
    }
  }
</style>
